import {
  aiModelsResultSchema,
  aiProviderListSchema,
  aiProviderSchema,
  aiTestConnectionResultSchema,
  aiVisionTestResultSchema,
  type AiModelsResult,
  type AiProbeRequest,
  type AiProvider,
  type AiVisionTestRequest,
  type AiVisionTestResult,
  type CreateAiProviderRequest,
  type AiTestConnectionResult,
  type UpdateAiProviderRequest,
} from '@xphoto/shared';
import { LONG_TIMEOUT_MS, apiFetch } from '../lib/http';

/**
 * Client typé de la CONFIGURATION LLM (T5 lot A, XPHOTO-26). Chaque réponse est
 * RE-PARSÉE via son schéma partagé `@xphoto/shared` (« never trust the client »).
 *
 * ⚠ Aucune clé d'API ne transite ici : seul le NOM de sa variable d'env
 * (`apiKeyEnv`). Le serveur renvoie `apiKeyPresent` (booléen) — jamais la valeur.
 * Les cookies de session sont same-origin ; aucun jeton n'est manipulé en JS.
 */

/** Erreur d'appel API : statut HTTP + code d'erreur normalisé. */
export class AiApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'AiApiError';
  }
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON (ou 204 sans corps) : code générique.
  }
  return 'UNKNOWN';
}

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) throw new AiApiError(res.status, await errorCodeOf(res));
  return res.json();
}

/** `GET /api/libraries/:id/ai-providers` — liste des fournisseurs configurés. */
export async function fetchAiProviders(
  libraryId: string,
  signal?: AbortSignal,
): Promise<AiProvider[]> {
  const res = await apiFetch(`/api/libraries/${libraryId}/ai-providers`, {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  return aiProviderListSchema.parse(await readJson(res)).providers;
}

/** `POST /api/libraries/:id/ai-providers` — crée un fournisseur (201). */
export async function createAiProvider(
  libraryId: string,
  input: CreateAiProviderRequest,
): Promise<AiProvider> {
  const res = await apiFetch(`/api/libraries/${libraryId}/ai-providers`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return aiProviderSchema.parse(await readJson(res));
}

/** `PATCH /api/libraries/:id/ai-providers/:providerId` — met à jour / active. */
export async function updateAiProvider(
  libraryId: string,
  providerId: string,
  patch: UpdateAiProviderRequest,
): Promise<AiProvider> {
  const res = await apiFetch(`/api/libraries/${libraryId}/ai-providers/${providerId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return aiProviderSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/ai-providers/test-connection` — teste un fournisseur NON
 * encore enregistré (l'écran teste avant d'enregistrer). Toujours 200 + résultat
 * structuré (un fournisseur injoignable ne fait pas échouer la requête).
 */
export async function testAiConnection(
  libraryId: string,
  probe: AiProbeRequest,
): Promise<AiTestConnectionResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/ai-providers/test-connection`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(probe),
    // SONDE LLM : un modèle local à froid (LM Studio/Ollama) met couramment
    // plusieurs minutes à répondre au premier appel → plafond long, pas le défaut.
    timeoutMs: LONG_TIMEOUT_MS,
  });
  return aiTestConnectionResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/ai-providers/test-vision` — DOUBLE TEST du modèle : (A) il
 * répond à une invite texte, (B) il accepte une mini-image (vision). Toujours 200 +
 * résultat structuré (un modèle texte-seul renvoie `imageAccepted:false` + message).
 */
export async function testAiVision(
  libraryId: string,
  probe: AiVisionTestRequest,
): Promise<AiVisionTestResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/ai-providers/test-vision`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(probe),
    // SONDE LLM : un modèle local à froid (LM Studio/Ollama) met couramment
    // plusieurs minutes à répondre au premier appel → plafond long, pas le défaut.
    timeoutMs: LONG_TIMEOUT_MS,
  });
  return aiVisionTestResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/ai-providers/discover-models` — découvre les modèles
 * disponibles auprès d'un fournisseur (peuple le `<select>` de modèle).
 */
export async function discoverAiModels(
  libraryId: string,
  probe: AiProbeRequest,
): Promise<AiModelsResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/ai-providers/discover-models`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(probe),
    // SONDE LLM : un modèle local à froid (LM Studio/Ollama) met couramment
    // plusieurs minutes à répondre au premier appel → plafond long, pas le défaut.
    timeoutMs: LONG_TIMEOUT_MS,
  });
  return aiModelsResultSchema.parse(await readJson(res));
}
